import { t } from '@/lib/i18n';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Feedback.module.css';
import { CloseIcon } from '@/components/icons';

// ------------------------------------------------------------ InsightCard

interface InsightCardProps {
  children: ReactNode;
  /**
   * The sample the claim rests on, e.g. "em 12 voltas aqui". Required by design:
   * canvas 1a says an insight always carries its base. A trend drawn from two rounds
   * is a coincidence, and saying so is the difference between insight and noise.
   */
  sample: string;
  tone?: 'ink' | 'light';
  onDismiss?: () => void;
}

export function InsightCard({ children, sample, tone = 'ink', onDismiss }: InsightCardProps) {
  return (
    <aside className={cx(styles.insight, tone === 'light' && styles.insightLight)}>
      <span className={styles.insightIcon} aria-hidden="true">
        i
      </span>
      <div className={styles.insightBody}>
        <p className={styles.insightText}>{children}</p>
        <p className={styles.insightSample}>{sample}</p>
      </div>
      {onDismiss && (
        <button
          type="button"
          className={styles.insightClose}
          onClick={onDismiss}
          aria-label={t('common.dismiss')}
        >
          <CloseIcon size={18} />
        </button>
      )}
    </aside>
  );
}

// ------------------------------------------------------------- ErrorBlock

interface ErrorBlockProps {
  title: string;
  children?: ReactNode;
  /** Seconds remaining. Renders the countdown bar used for a 429. */
  secondsLeft?: number;
  totalSeconds?: number;
}

export function ErrorBlock({ title, children, secondsLeft, totalSeconds = 60 }: ErrorBlockProps) {
  return (
    <div className={styles.errorBlock} role="alert">
      <p className={styles.errorTitle}>{title}</p>
      {children != null && <p className={styles.errorBody}>{children}</p>}
      {secondsLeft != null && (
        <div className={styles.countdown}>
          <div className={styles.countdownTrack}>
            <div
              className={styles.countdownFill}
              style={{ width: `${Math.max(0, (secondsLeft / totalSeconds) * 100)}%` }}
            />
          </div>
          <span className={styles.countdownValue}>
            {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
          </span>
        </div>
      )}
    </div>
  );
}

/** Ticks a countdown down to zero and reports when it lands. */
export function useCountdown(seconds: number | null, onDone?: () => void): number | null {
  const [left, setLeft] = useState(seconds);

  useEffect(() => setLeft(seconds), [seconds]);

  useEffect(() => {
    if (left == null || left <= 0) return;
    const id = window.setInterval(() => {
      setLeft((v) => {
        if (v == null) return v;
        if (v <= 1) {
          window.clearInterval(id);
          onDone?.();
          return 0;
        }
        return v - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
    // onDone is intentionally not a dependency — it would restart the timer on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left == null, seconds]);

  return left;
}

// ---------------------------------------------------------- OfflineBanner

export function OfflineBanner() {
  return (
    <div className={styles.offline} role="status">
      <span className={styles.offlineDot} aria-hidden="true" />
      <span>{t('common.offline')}</span>
    </div>
  );
}

/** Tracks browser connectivity. Coarse, but it is what `navigator.onLine` can tell us. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

// ---------------------------------------------------------------- Skeleton

/**
 * A placeholder shaped like the content it replaces. The canvas bans full-page spinners
 * outright — "Nunca spinner de tela. Spinner só inline, em botão." — because the stats
 * screen has to walk every round one by one and is the longest wait in the app.
 */
export function Skeleton({
  width = '100%',
  height = 12,
  radius,
  className,
  style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cx(styles.skeleton, className)}
      style={{ width, height, borderRadius: radius, ...style }}
      aria-hidden="true"
    />
  );
}

// -------------------------------------------------------------- EmptyState

export function EmptyState({
  title,
  children,
  actions,
}: {
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyRing} aria-hidden="true">
        <span className={styles.emptyBall} />
      </div>
      <h2 className={styles.emptyTitle}>{title}</h2>
      {children != null && <p className={styles.emptyBody}>{children}</p>}
      {actions != null && <div className={styles.emptyActions}>{actions}</div>}
    </div>
  );
}
